// src/utils/dateControlPeriods.ts
//
// A date range control sets the period of the tiles it reaches (plan
// 2026-09-29 controls A4b). Pure, so the rules are tested without mounting
// Insights; `tilePeriod.ts` holds the other source of a tile's own period
// (a period click), which applies on top of this one.
//
// - Reach: the data tiles of the control's page in its interactions scope
//   ("auto" = every one), like every other control. A text, a heading or a
//   control has no period to set; a tile with a period of its own is never
//   reached either: fixed is fixed (ADR-0445 decision 5).
// - One period per tile, never a silent winner: the backend refuses two
//   date range controls that reach one tile. A page that came to overlap
//   anyway (a copy, a hand-made spec) gives the tile to the FIRST control in
//   page order, and the other one says so on its block.
// - The period: the viewer's choice in the URL (`?c.<block_key>=`, the
//   document the per-dashboard memory stores, validated by the same rules),
//   else the control's default preset with the dashboard's comparison, else
//   none: the control follows the dashboard period and moves no tile.
import { format } from "date-fns";
import { CONTROL_PARAM_PREFIX } from "@/utils/controlChoiceUrl";
import { getPresetDates, parseRollingPreset, type TimeRange, type TimeRangePreset } from "@/utils/comparisonWindow";
import { restoreDashboardPeriodDocument, serialiseDashboardPeriod } from "@/utils/dashboardPeriodMemory";
import { interactionTargetsOf } from "@/utils/tabFilters";
import { isControlSpec } from "@/components/dashboards/composition/blocks";
import { dateRangeControlOfSpec } from "@/components/dashboards/composition/openControlSpec";

/** The "field" a date range control steers in the Edit interactions mode and
 *  in Applies to: every tile with a period can take it. */
export const PERIOD_DIMENSION = { key: "__period__", label: "period" } as const;

type PageItem = {
  id: string;
  block_key?: string;
  chart_spec_json?: Record<string, unknown> | null;
  query_ir_json?: unknown;
  query_sql?: unknown;
  /** The block's own period (ADR-0445); null or absent = it follows the
   *  dashboard. */
  period?: unknown;
};

export type DateControl = {
  id: string;
  blockKey: string;
  /** The period it sets now; null = it follows the dashboard period. */
  period: TimeRange | null;
  /** The block keys of the tiles it reaches and was not beaten to. */
  reach: ReadonlySet<string>;
  /** Tiles in its scope that an earlier date control already reaches. */
  shadowed: number;
};

/** Does `item` refresh over a period: a data tile, not a block without data. */
export function isPeriodTile(item: Pick<PageItem, "chart_spec_json" | "query_ir_json" | "query_sql">): boolean {
  return Boolean(item.query_ir_json || item.query_sql) && !isControlSpec(item.chart_spec_json ?? null);
}

/** Does `item` carry a period of its own (ADR-0445): then neither the header
 *  nor a date range control reaches it. */
export function hasOwnPeriod(item: Pick<PageItem, "period">): boolean {
  return item.period != null;
}

/** Can a date range control set this tile's period: a data tile without a
 *  period of its own (ADR-0445 decision 5). */
export function isDateControlReachable(
  item: Pick<PageItem, "chart_spec_json" | "query_ir_json" | "query_sql" | "period">,
): boolean {
  return isPeriodTile(item) && !hasOwnPeriod(item);
}

/** Two periods that put the same thing on screen: same preset and window,
 *  same comparison and its window, same target / other-period switches. */
export function sameTimeRange(a: TimeRange, b: TimeRange): boolean {
  const day = (d?: Date) => (d ? format(d, "yyyy-MM-dd") : "");
  return (
    a.preset === b.preset
    && day(a.startDate) === day(b.startDate)
    && day(a.endDate) === day(b.endDate)
    && a.comparisonMode === b.comparisonMode
    && day(a.comparisonStartDate) === day(b.comparisonStartDate)
    && day(a.comparisonEndDate) === day(b.comparisonEndDate)
    && (a.withTarget === true) === (b.withTarget === true)
    && (a.withOtherPeriod === true) === (b.withOtherPeriod === true)
  );
}

/** The URL value of a chosen period. */
export function dateControlParamValue(range: TimeRange): string {
  return JSON.stringify(serialiseDashboardPeriod(range));
}

/** A URL value back into a period, or null when it is not a valid one. */
export function parseDateControlParam(raw: string | null | undefined): TimeRange | null {
  if (!raw) return null;
  try {
    return restoreDashboardPeriodDocument(JSON.parse(raw));
  } catch {
    return null;
  }
}

/** The control's default preset over the dashboard's comparison. A custom
 *  comparison names dates for the dashboard's window, so it becomes the
 *  previous period here. */
export function defaultPresetPeriod(preset: TimeRangePreset, header: TimeRange): TimeRange | null {
  if (preset === "custom") return null;
  if (preset.startsWith("rolling_") && !parseRollingPreset(preset)) return null;
  let dates: { startDate: Date; endDate: Date };
  try {
    dates = getPresetDates(preset);
  } catch {
    return null;
  }
  const custom = header.comparisonMode === "custom";
  return {
    ...header,
    preset,
    ...dates,
    comparisonMode: custom ? "previous_period" : header.comparisonMode,
    comparisonStartDate: undefined,
    comparisonEndDate: undefined,
  };
}

/** The period a control rests on without a viewer's choice: its default,
 *  else null (it follows the dashboard). */
export function restingPeriodOf(spec: Record<string, unknown> | null | undefined, header: TimeRange): TimeRange | null {
  const preset = dateRangeControlOfSpec(spec)?.default ?? null;
  return preset ? defaultPresetPeriod(preset, header) : null;
}

/** The date range controls on a page, in page order, each with its period
 *  and the tiles it reaches. `params` holds the viewer's choices. */
export function dateControlsOf(
  items: readonly PageItem[],
  params: URLSearchParams,
  header: TimeRange,
): DateControl[] {
  const periodTiles = items.filter(isDateControlReachable).flatMap((i) => (i.block_key ? [i.block_key] : []));
  const taken = new Set<string>();
  const out: DateControl[] = [];
  for (const item of items) {
    if (!dateRangeControlOfSpec(item.chart_spec_json ?? null) || !item.block_key) continue;
    const chosen = parseDateControlParam(params.get(`${CONTROL_PARAM_PREFIX}${item.block_key}`));
    const period = chosen ?? restingPeriodOf(item.chart_spec_json, header);
    const targets = interactionTargetsOf(item.chart_spec_json);
    const scope = periodTiles.filter((k) => targets === "auto" || targets.includes(k));
    const reach = new Set(scope.filter((k) => !taken.has(k)));
    for (const k of reach) taken.add(k);
    out.push({
      id: item.id,
      blockKey: item.block_key,
      period: period && !sameTimeRange(period, header) ? period : null,
      reach,
      shadowed: scope.length - reach.size,
    });
  }
  return out;
}

/** Block key -> the period a date control sets on that tile; tiles that
 *  follow the dashboard period are absent. */
export function tileDatePeriods(controls: readonly DateControl[]): Map<string, TimeRange> {
  const out = new Map<string, TimeRange>();
  for (const control of controls) {
    if (!control.period) continue;
    for (const key of control.reach) out.set(key, control.period);
  }
  return out;
}

/** A stable key of the periods per tile, for dependency arrays and view keys. */
export function tileDatePeriodsKey(periods: ReadonlyMap<string, TimeRange>): string {
  return JSON.stringify(
    [...periods.entries()]
      .map(([key, range]) => [key, dateControlParamValue(range), format(range.startDate, "yyyy-MM-dd"), format(range.endDate, "yyyy-MM-dd")])
      .sort(([a], [b]) => (a < b ? -1 : 1)),
  );
}

/** The URL after the viewer set the control `blockKey` to `range`: the
 *  period it rests on (its default, else the dashboard's) removes the
 *  parameter. */
export function withDateControlParam(
  prev: URLSearchParams,
  blockKey: string,
  range: TimeRange,
  resting: TimeRange,
): URLSearchParams {
  const next = new URLSearchParams(prev);
  const name = `${CONTROL_PARAM_PREFIX}${blockKey}`;
  next.delete(name);
  if (!sameTimeRange(range, resting)) next.set(name, dateControlParamValue(range));
  return next;
}
